/**
 * Stat cards for run processing statistics.
 */

import Box from "@cloudscape-design/components/box"
import ColumnLayout from "@cloudscape-design/components/column-layout"
import Container from "@cloudscape-design/components/container"
import Header from "@cloudscape-design/components/header"
import type { RunStatistics } from "@/generated/sdk"

interface ProcessingStatisticsProps {
  /**
   * The run's processing statistics.
   */
  statistics: RunStatistics
  /**
   * Whether this was a Test Run (hides insert/update/unchanged stats).
   */
  isDryRun: boolean
}

/**
 * Renders stat cards showing record counts (processed, quarantined, inserted, etc.) with colored
 * variants for key metrics.
 */
export default function ProcessingStatistics({ statistics, isDryRun }: ProcessingStatisticsProps) {
  return (
    <Container header={<Header variant="h3">Processing Statistics</Header>}>
      <ColumnLayout columns={isDryRun ? 2 : 3} variant="text-grid">
        <Box>
          <Box variant="awsui-key-label">Processed</Box>
          <Box variant="awsui-value-large">{statistics.recordsProcessed}</Box>
        </Box>
        {!isDryRun && (
          <>
            <Box>
              <Box variant="awsui-key-label">Inserted</Box>
              <Box variant="awsui-value-large" color="text-status-success">
                {statistics.recordsInserted}
              </Box>
            </Box>
            <Box>
              <Box variant="awsui-key-label">Updated</Box>
              <Box variant="awsui-value-large" color="text-status-info">
                {statistics.recordsUpdated}
              </Box>
            </Box>
            <Box>
              <Box variant="awsui-key-label">Unchanged</Box>
              <Box variant="awsui-value-large">{statistics.recordsUnchanged}</Box>
            </Box>
          </>
        )}
        <Box>
          <Box variant="awsui-key-label">Quarantined</Box>
          <Box variant="awsui-value-large" color="text-status-error">
            {statistics.recordsQuarantined}
          </Box>
        </Box>
        {statistics.recordsDeduplicated > 0 && (
          <Box>
            <Box variant="awsui-key-label">Deduplicated</Box>
            <Box variant="awsui-value-large">{statistics.recordsDeduplicated}</Box>
          </Box>
        )}
      </ColumnLayout>
    </Container>
  )
}
